// ============================================================
// home-search/search-flow.js — Flujo completo de búsqueda
// ============================================================

import { apiConfig } from '../api-config.js';
import { buscarNegociosInteligente } from '../search.js';
import { normalizeText, detectCountry, matchesCountry } from './utils.js';
import { isPremium } from './premium-detector.js';
import { openPremiumWindowForBusiness } from './premium-card.js';
import { renderResultados } from './results-renderer.js';
import { testBackend } from './backend-banner.js';

// Referencias al DOM (se setean desde el orquestador)
let _inputBusqueda = null;
let _inputCiudad = null;

/**
 * Inicializa las referencias DOM para el flujo de búsqueda
 */
export function initSearchFlow(inputBusqueda, inputCiudad) {
  _inputBusqueda = inputBusqueda;
  _inputCiudad = inputCiudad;
}

/**
 * Busca coincidencia exacta de negocio por nombre
 */
async function buscarCoincidenciaExacta(termino, categoria = '', ciudad = '', pais = '', paisCode = '') {
  try {
    const response = await fetch(`${apiConfig.baseURL}/businesses?limit=50`);
    if (response.ok) {
      const businessData = await response.json();
      let allBusinesses = businessData.data?.businesses || [];

      const nTerm = normalizeText(termino);
      let exactMatch = allBusinesses.find(biz =>
        normalizeText(biz.name || '') === nTerm
      );

      if (!exactMatch) {
        exactMatch = allBusinesses.find(biz => {
          const bizName = normalizeText(biz.name || '');
          return bizName.includes(nTerm) && bizName.length - nTerm.length <= 3;
        });
      }

      if (exactMatch) {
        let matchesFilters = true;

        if (categoria) {
          const cat = categoria.toLowerCase();
          matchesFilters = matchesFilters && (exactMatch.category && (exactMatch.category.toLowerCase() === cat || (exactMatch.category_slug && exactMatch.category_slug.toLowerCase() === cat)));
        }

        if (ciudad) {
          const cz = normalizeText(ciudad);
          matchesFilters = matchesFilters && normalizeText(exactMatch.city || '').includes(cz);
        }

        if (pais && !ciudad) {
          matchesFilters = matchesFilters && matchesCountry(exactMatch, pais, paisCode);
        }

        if (matchesFilters) {
          console.log('🎯 Coincidencia exacta encontrada:', exactMatch.name, 'Premium:', isPremium(exactMatch));

          if (isPremium(exactMatch)) {
            console.log('✅ Negocio premium encontrado, mostrando solo su tarjeta');
            openPremiumWindowForBusiness(exactMatch);
            return true;
          } else {
            console.log('📍 Negocio no premium, buscando su subcategoría');
            const subcategoria = exactMatch.subcategory || exactMatch.category;
            if (subcategoria) {
              let categoryBusinesses = allBusinesses.filter(biz =>
                (biz.subcategory === subcategoria) ||
                (biz.category === subcategoria) ||
                (biz.category === exactMatch.category)
              );

              if (ciudad) {
                const cz = normalizeText(ciudad);
                categoryBusinesses = categoryBusinesses.filter(b => normalizeText(b.city || '').includes(cz));
              }
              if (pais && !ciudad) {
                categoryBusinesses = categoryBusinesses.filter(b => matchesCountry(b, pais, paisCode));
              }

              categoryBusinesses = [exactMatch, ...categoryBusinesses.filter(b => b.id !== exactMatch.id)];
              window.__scrollToBizName = normalizeText(exactMatch.name || '');

              renderResultados({
                results: categoryBusinesses,
                etiquetas_encontradas: [termino],
                message: `Mostrando negocios de ${subcategoria} - ${exactMatch.name} encontrado`
              });
              return true;
            }
          }
        }
      }
    }
    return false;
  } catch (error) {
    console.error('💥 Error buscando coincidencia exacta:', error);
    return false;
  }
}

/**
 * Busca negocios por categorías relacionadas cuando no hay resultados específicos
 */
async function buscarPorCategorias(etiquetas, terminoOriginal, categoriaSeleccionada = '', ciudad = '', pais = '', paisCode = '') {
  try {
    console.log('🔍 Buscando por etiquetas:', etiquetas);

    const etiquetaCategoria = {
      'fotografía': 'entretenimiento', 'foto': 'entretenimiento', 'video': 'entretenimiento',
      'evento': 'entretenimiento', 'música': 'entretenimiento',
      'tecnología': 'tecnologia', 'it': 'tecnologia', 'desarrollo': 'tecnologia',
      'educación': 'educacion', 'escuela': 'educacion', 'academia': 'educacion',
      'gastronomía': 'gastronomia', 'restaurante': 'gastronomia', 'comida': 'gastronomia'
    };

    let categoriaEncontrada = null;
    for (const etiqueta of etiquetas) {
      const etiquetaLower = etiqueta.toLowerCase();
      categoriaEncontrada = etiquetaCategoria[etiquetaLower];
      if (categoriaEncontrada) break;

      for (const [key, value] of Object.entries(etiquetaCategoria)) {
        if (etiquetaLower.includes(key) || key.includes(etiquetaLower)) {
          categoriaEncontrada = value;
          break;
        }
      }
      if (categoriaEncontrada) break;
    }

    const categoriaObjetivo = (categoriaSeleccionada && categoriaSeleccionada.trim()) ? categoriaSeleccionada.trim() : categoriaEncontrada;
    console.log('🎯 Categoría objetivo:', categoriaObjetivo);

    if (categoriaObjetivo) {
      const urlsToTry = [
        `${apiConfig.baseURL}/businesses?category=${categoriaObjetivo}&limit=20`,
        `${apiConfig.baseURL}/businesses?limit=50`
      ];

      for (const url of urlsToTry) {
        console.log('🔗 Probando URL:', url);
        const response = await fetch(url);
        if (response.ok) {
          const businessData = await response.json();
          let businesses = businessData.data?.businesses || [];

          if (url.includes('limit=50')) {
            businesses = businesses.filter(biz =>
              biz.category === categoriaObjetivo ||
              biz.category_slug === categoriaObjetivo ||
              (biz.category && biz.category.toLowerCase().includes(categoriaObjetivo))
            );
          }
          if (ciudad) {
            const cz = normalizeText(ciudad);
            businesses = businesses.filter(b => normalizeText(b.city || '').includes(cz));
          }
          if (pais && !ciudad) {
            businesses = businesses.filter(b => matchesCountry(b, pais, paisCode));
          }

          console.log('📦 Negocios encontrados:', businesses.length);

          if (businesses.length > 0) {
            const nTerm = normalizeText(terminoOriginal);
            const matchingBiz = businesses.find(b => normalizeText(b.name || '').includes(nTerm));
            if (matchingBiz) {
              console.log('📍 Posicionando negocio coincidente primero:', matchingBiz.name);
              businesses = [matchingBiz, ...businesses.filter(b => b.id !== matchingBiz.id)];
              window.__scrollToBizName = normalizeText(matchingBiz.name || '');
            }

            renderResultados({
              results: businesses,
              etiquetas_encontradas: etiquetas,
              message: null
            });
            return true;
          }
        }
      }
    }

    return false;
  } catch (error) {
    console.error('💥 Error buscando por categorías:', error);
    return false;
  }
}

/**
 * Busca negocios por nombre
 */
async function buscarPorNombre(termino, categoria = '', ciudad = '', pais = '', paisCode = '') {
  try {
    const response = await fetch(`${apiConfig.baseURL}/businesses?limit=50`);
    if (response.ok) {
      const businessData = await response.json();
      let allBusinesses = businessData.data?.businesses || [];

      let businesses = allBusinesses.filter(biz =>
        biz.name && biz.name.toLowerCase().includes(termino.toLowerCase())
      );
      if (categoria) {
        const cat = categoria.toLowerCase();
        businesses = businesses.filter(b => (b.category && (b.category.toLowerCase() === cat || (b.category_slug && b.category_slug.toLowerCase() === cat))));
      }
      if (ciudad) {
        const cz = normalizeText(ciudad);
        businesses = businesses.filter(b => normalizeText(b.city || '').includes(cz));
      }
      if (pais && !ciudad) {
        businesses = businesses.filter(b => matchesCountry(b, pais, paisCode));
      }

      console.log(`📝 Búsqueda por nombre "${termino}":`, businesses.length, 'resultados');

      if (businesses.length > 0) {
        try {
          if (!window.__scrollToBizName) {
            const nTerm = normalizeText(termino);
            const target = businesses.find(b => normalizeText(b.name || '').includes(nTerm)) || businesses[0];
            if (target) window.__scrollToBizName = normalizeText(target.name || '');
          }
        } catch (_) { /* noop */ }

        renderResultados({
          results: businesses,
          etiquetas_encontradas: [termino],
          message: null
        });
      } else {
        renderResultados({ results: [], etiquetas_encontradas: [termino], message: 'No se encontraron negocios.' });
      }
    } else {
      renderResultados({ results: [], message: 'Error en la búsqueda.' });
    }
  } catch (error) {
    console.error('💥 Error buscando por nombre:', error);
    renderResultados({ results: [], message: 'Error en la búsqueda.' });
  }
}

/**
 * Función principal de búsqueda (llamada desde botón o Enter)
 */
export async function hacerBusqueda() {
  if (!_inputBusqueda || !_inputCiudad) return;

  let termino = _inputBusqueda.value.trim();
  termino = typeof window.Sanitizer !== 'undefined' ? window.Sanitizer.sanitizeSQL(termino) : termino;

  const categoriaEl = document.querySelector('.search-card select');
  const categoria = categoriaEl ? (() => {
    const v = (categoriaEl.value || '').trim().toLowerCase();
    return (v === 'seleccionar categoria' || v === 'seleccionar categoría') ? '' : v;
  })() : '';

  const cityRaw = _inputCiudad.value.trim();
  let ciudad = cityRaw;
  let pais = '';
  let paisCode = '';

  if (cityRaw.includes(',')) {
    const [c, p] = cityRaw.split(',');
    ciudad = c.trim();
    pais = p.trim();
    const det = detectCountry(pais);
    if (det) { pais = det.name; paisCode = det.code; }
  } else if (cityRaw) {
    const det = detectCountry(cityRaw);
    if (det) {
      ciudad = '';
      pais = det.name;
      paisCode = det.code;
    }
  }

  if (!pais && ciudad) {
    const det2 = detectCountry(ciudad);
    if (det2) {
      pais = det2.name;
      paisCode = det2.code;
      ciudad = '';
    }
  }

  if (!termino) return;

  const container = document.querySelector('#resultados-busqueda-inteligente') || (() => {
    const c = document.createElement('div');
    c.id = 'resultados-busqueda-inteligente';
    c.style.cssText = 'max-width:800px;margin:2rem auto;background:#fff;border-radius:1.5rem;box-shadow:0 8px 32px rgba(74,144,226,0.08);padding:2rem;display:none;';
    const hero = document.querySelector('.hero');
    if (hero) hero.insertAdjacentElement('afterend', c);
    return c;
  })();

  container.textContent = '';
  const loading = document.createElement('div');
  loading.style.color = '#2563eb';
  loading.textContent = 'Buscando...';
  container.appendChild(loading);
  container.style.display = 'block';

  // Paso 0: Coincidencia exacta
  console.log('🎯 Paso 0: Buscando coincidencia exacta de negocio...');
  const exactMatch = await buscarCoincidenciaExacta(termino, categoria, ciudad, pais, paisCode);
  if (exactMatch) return;

  // Paso 1: Búsqueda inteligente
  console.log('🧠 Paso 1: Búsqueda inteligente...');
  const data = await buscarNegociosInteligente(termino, categoria, ciudad, pais).catch(() => ({ results: [], etiquetas_encontradas: [], message: 'Error en la búsqueda' }));
  if (!data || data.error) {
    testBackend();
  }

  if (data.results && data.results.length > 0) {
    let filtered = data.results;
    if (categoria) {
      const cat = categoria.toLowerCase();
      filtered = filtered.filter(b => (b.category && (b.category.toLowerCase() === cat || (b.category_slug && b.category_slug.toLowerCase() === cat))));
    }
    if (ciudad) {
      const cz = normalizeText(ciudad);
      filtered = filtered.filter(b => normalizeText(b.city || '').includes(cz));
    }
    if (pais && !ciudad) {
      filtered = filtered.filter(b => matchesCountry(b, pais, paisCode));
    }

    console.log('✅ Búsqueda inteligente exitosa:', filtered.length, 'resultados');

    const nTerm = normalizeText(termino);
    const exactInResults = filtered.find(b => normalizeText(b.name || '') === nTerm);
    if (exactInResults) {
      console.log('🎯 Coincidencia exacta encontrada en búsqueda inteligente:', exactInResults.name);
      if (isPremium(exactInResults)) {
        console.log('✅ Negocio premium encontrado, mostrando solo su tarjeta');
        openPremiumWindowForBusiness(exactInResults);
        return;
      } else {
        console.log('📍 Negocio no premium, posicionándolo primero en resultados');
        filtered = [exactInResults, ...filtered.filter(b => b.id !== exactInResults.id)];
        window.__scrollToBizName = normalizeText(exactInResults.name || '');
      }
    }

    renderResultados({
      results: filtered,
      etiquetas_encontradas: data.etiquetas_encontradas,
      message: null
    });
    return;
  }

  // Paso 2: Buscar por categorías
  if (data.etiquetas_encontradas && data.etiquetas_encontradas.length > 0) {
    console.log('🔄 Paso 2: Buscando por categorías relacionadas...');
    const categoriaEncontrada = await buscarPorCategorias(data.etiquetas_encontradas, termino, categoria, ciudad, pais, paisCode);
    if (categoriaEncontrada) return;
  }

  // Paso 3: Buscar por nombre
  console.log('🔤 Paso 3: Buscando por nombre de negocio...');
  await buscarPorNombre(termino, categoria, ciudad, pais, paisCode);
}
